import React, { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, Loader2, CheckCircle2, AlertTriangle, Copy } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/common/StatusChip";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const PLANTILLA =
  "nombre,empresa,telefono,email,especialidades,canal_preferido,sabe_leer,modalidad_liquidacion,tarifa_promedio,idioma,notas\n" +
  "Juan Perez,Perez Painting,(415) 555-9001,juan@mail.com,Pintura interior;Limpieza / Aseo,whatsapp,si,quincenal,420.00,es,\n" +
  "Pedro Lopez,,(510) 555-9002,,Plomería,voz,no,semanal,350.00,es,No sabe leer\n";

export const SubImportDialog = ({ open, onOpenChange }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const close = () => {
    setPreview(null);
    onOpenChange(false);
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/subs/import/preview", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const rows = (preview?.rows || []).filter((r) => r.valido);
      const { data } = await api.post("/subs/import/confirm", { rows });
      toast.success(t("subs.import.done", { n: data.creados }));
      qc.invalidateQueries({ queryKey: ["subs"] });
      close();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  const copyTemplate = async () => {
    try {
      await navigator.clipboard.writeText(PLANTILLA);
      toast.success(t("subs.import.template_copied"));
    } catch (e) {
      /* clipboard bloqueado */
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent data-testid="sub-import-dialog" className="max-h-[92vh] max-w-[900px] overflow-hidden rounded-[10px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{t("subs.import.title")}</DialogTitle>
        </DialogHeader>

        {!preview ? (
          <div className="space-y-3">
            <div
              data-testid="sub-import-dropzone"
              onClick={() => fileRef.current?.click()}
              className="flex cursor-pointer flex-col items-center gap-2 rounded-[10px] border-2 border-dashed border-line-strong bg-surface-sunken px-6 py-10 text-center transition-colors hover:border-brand hover:bg-brand-soft"
            >
              {busy ? (
                <Loader2 className="h-6 w-6 animate-spin text-brand" />
              ) : (
                <Upload className="h-6 w-6 text-ink-tertiary" />
              )}
              <p className="text-[13.5px] font-medium text-ink">{t("subs.import.pick")}</p>
              <p className="text-[12px] text-ink-tertiary">{t("subs.import.help")}</p>
            </div>
            <input
              ref={fileRef}
              data-testid="sub-import-file"
              type="file"
              accept=".csv,.xlsx"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
            />
            <div className="rounded-[8px] border border-line bg-white p-3">
              <div className="flex items-center justify-between">
                <p className="text-[12.5px] font-medium text-ink">{t("subs.import.columns")}</p>
                <Button variant="outline" size="sm" data-testid="sub-import-copy-template"
                  onClick={copyTemplate} className="h-7 rounded-[6px] text-[11.5px]">
                  <Copy className="mr-1.5 h-3 w-3" /> {t("subs.import.copy_template")}
                </Button>
              </div>
              <pre className="mt-2 overflow-x-auto rounded-[6px] bg-ink p-2.5 text-[11px] leading-relaxed text-line">
{PLANTILLA}
              </pre>
            </div>
          </div>
        ) : (
          <div className="min-h-0 flex-1 space-y-3 overflow-hidden">
            <div className="flex flex-wrap items-center gap-2" data-testid="sub-import-counters">
              <StatusChip tone="green" testId="sub-import-valid" label={`${preview.validas} ${t("subs.import.valid")}`} />
              <StatusChip tone="amber" testId="sub-import-dupes" label={`${preview.duplicadas} ${t("subs.import.dupes")}`} />
              <StatusChip tone="red" testId="sub-import-errors" label={`${preview.con_error} ${t("subs.import.errors")}`} />
              <span className="text-[12px] text-ink-tertiary">{preview.total} {t("subs.import.rows")}</span>
            </div>
            <div className="max-h-[46vh] overflow-auto rounded-[8px] border border-line">
              <table className="w-full text-[12.5px]" data-testid="sub-import-table">
                <thead className="sticky top-0 bg-surface-sunken">
                  <tr className="border-b border-line text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
                    <th className="px-2.5 py-2 text-left">#</th>
                    <th className="px-2.5 py-2 text-left">{t("app.name")}</th>
                    <th className="px-2.5 py-2 text-left">{t("app.phone")}</th>
                    <th className="px-2.5 py-2 text-left">{t("subs.specialties")}</th>
                    <th className="px-2.5 py-2 text-left">{t("subs.channel")}</th>
                    <th className="px-2.5 py-2 text-right">{t("subs.rate")}</th>
                    <th className="px-2.5 py-2 text-left">{t("app.status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.linea} data-testid={`sub-import-row-${r.linea}`}
                      className={`h-[38px] border-b border-line last:border-0 ${r.valido ? "" : "bg-state-critical-soft"}`}>
                      <td className="px-2.5 tabular-nums text-ink-tertiary">{r.linea}</td>
                      <td className="px-2.5 font-medium">{r.nombre || "—"}</td>
                      <td className="px-2.5 tabular-nums">{r.telefono || "—"}</td>
                      <td className="px-2.5 text-ink-secondary">{(r.especialidades_nombres || []).join(", ") || r.especialidades_texto || "—"}</td>
                      <td className="px-2.5">{t(`channels.${r.canal_preferido}`)}</td>
                      <td className="px-2.5 text-right tabular-nums">{formatMoney(r.tarifa_promedio_cents)}</td>
                      <td className="px-2.5">
                        {r.valido ? (
                          <span className="inline-flex items-center gap-1 text-[11.5px] text-state-ok">
                            <CheckCircle2 className="h-3.5 w-3.5" /> {t("subs.import.ok")}
                          </span>
                        ) : (
                          <span className="inline-flex items-start gap-1 text-[11.5px] text-state-critical">
                            <AlertTriangle className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                            {r.errores.join(" ")}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          {preview && (
            <Button variant="outline" data-testid="sub-import-back" onClick={() => setPreview(null)}
              className="h-9 rounded-[6px] text-[13px]">
              {t("app.back")}
            </Button>
          )}
          <Button variant="outline" onClick={close} className="h-9 rounded-[6px] text-[13px]">
            {t("app.cancel")}
          </Button>
          {preview && (
            <Button
              data-testid="sub-import-confirm"
              disabled={busy || !preview.validas}
              onClick={confirm}
              className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
            >
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t("subs.import.confirm", { n: preview.validas })}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
